'use client';

import {
  Box,
  lighten,
  List,
  ListItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';

import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import {
  ComponentBlocksConTableList,
  ComponentBlocksProsAndConsTableV2,
  ComponentBlocksProTableList,
} from '@/types';

import { StrapiImage } from '../../Utility/StrapiImage';

export function ProsAndConsTableV2({ block }: { block: ComponentBlocksProsAndConsTableV2 }) {
  const theme = useTheme();
  const { ProsAndConsTitle, TableRows = [] } = block;

  return (
    <CustomBackground
      backgroundOptions={(block?.ProsAndConsBackground ?? undefined) as StrapiBackgroundOptions}
      displayOptions={
        {
          PaddingTop: block?.ProsAndConsDisplayOptions?.PaddingTop ?? 'md',
          PaddingBottom: block?.ProsAndConsDisplayOptions?.PaddingBottom ?? 'md',
          PaddingInline: block?.ProsAndConsDisplayOptions?.PaddingInline ?? 'sm',
        } as StrapiDisplayOptions
      }
      boxProps={{
        // @ts-expect-error - this does work, but types are not correct
        'data-component': 'ProsAndConsTableV2',
      }}
    >
      <Typography variant="h2" fontWeight={700} textAlign="center" mb={6}>
        {ProsAndConsTitle}
      </Typography>

      <TableContainer component={Paper} sx={{ borderRadius: 4, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: theme.palette.purple[900] }}>
              <TableCell sx={{ fontWeight: 700, color: theme.palette.common.white }}>
                Types of equipment finance
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: theme.palette.common.white }}>
                Pros
              </TableCell>
              <TableCell sx={{ fontWeight: 700, color: theme.palette.common.white }}>
                Cons
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {(TableRows ?? []).map((row, i) => (
              <TableRow
                key={i}
                sx={{
                  backgroundColor:
                    i % 2 === 0 ? theme.palette.common.white : theme.palette.purple['200'],
                  transition: 'background-color 0.3s',
                  '&:hover': {
                    backgroundColor: lighten(theme.palette.brand.brand, 0.9),
                  },
                }}
              >
                <TableCell sx={{ verticalAlign: 'top', fontWeight: 600, width: '25%' }}>
                  {row?.FinanceType ?? ''}
                </TableCell>

                <TableCell sx={{ verticalAlign: 'top', width: '37.5%' }}>
                  <ProsOrConsList list={row?.Pros?.filter(Boolean) ?? undefined} type="pro" />
                </TableCell>

                <TableCell sx={{ verticalAlign: 'top', width: '37.5%' }}>
                  <ProsOrConsList list={row?.Con?.filter(Boolean) ?? undefined} type="con" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </CustomBackground>
  );
}

function ProsOrConsList({ list = [], type }: { list?: any[]; type: 'pro' | 'con' }) {
  return (
    <List dense disablePadding>
      {list?.filter(Boolean).map((item, i) => {
        const title =
          type === 'pro'
            ? (item as ComponentBlocksProTableList).ProItemTitle
            : (item as ComponentBlocksConTableList).ConItemTitle;

        const desc =
          type === 'pro'
            ? (item as ComponentBlocksProTableList).ProItemDescription
            : (item as ComponentBlocksConTableList).ConItemDescription;

        const logo =
          type === 'pro'
            ? (item as ComponentBlocksProTableList).ProItemLogo
            : (item as ComponentBlocksConTableList).ConItemLogo;

        return (
          <ListItem key={i} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, pl: 0 }}>
            {logo && (
              <Box sx={{ mr: 1, mt: '0.25rem' }}>
                <StrapiImage image={logo} size="small" width={20} height={20} />
              </Box>
            )}
            <Box>
              <Typography variant="body1" fontWeight={700}>
                {!logo && <>•&nbsp;</>}
                {title}
              </Typography>
              {desc && <GlobalBlockRenderer content={desc} />}
            </Box>
          </ListItem>
        );
      })}
    </List>
  );
}
